import { useStageGridStore } from '../../models/stage-grid-state';
import './grid-lines.less';

const Rows = ({
    width,
    height,
    rows,
    rowSpace,
    topBottomSpace,
    leftRightSpace,
}: {
    width: number;
    height: number;
    rows: { value: number; visible: boolean };
    rowSpace: number;
    topBottomSpace: { value: number; visible: boolean };
    leftRightSpace: { value: number; visible: boolean };
}) => {
    const topBottomSpaceValue = topBottomSpace.visible ? topBottomSpace.value : 0;
    const leftRightSpaceValue = leftRightSpace.visible ? leftRightSpace.value : 0;
    const rowsHeight = (height - rowSpace * (rows.value - 1) - topBottomSpaceValue * 2) / rows.value;

    return (
        <div
            className="rows"
            style={{
                paddingLeft: `${leftRightSpaceValue}px`,
                paddingRight: `${leftRightSpaceValue}px`,
                paddingTop: `${topBottomSpaceValue}px`,
                paddingBottom: `${topBottomSpaceValue}px`,
            }}
        >
            {Array.from({ length: rows.value }).map((_, index) => (
                <div
                    key={index}
                    className="row"
                    style={{
                        width: '100%',
                        height: `${rowsHeight}px`,
                        backgroundColor: 'rgba(254, 0, 0, 0.2)',
                        marginBottom: `${rowSpace}px`,
                    }}
                ></div>
            ))}
        </div>
    );
};

const Columns = ({
    width,
    height,
    columns,
    columnSpace,
    leftRightSpace,
    topBottomSpace,
}: {
    width: number;
    height: number;
    columns: { value: number; visible: boolean };
    columnSpace: number;
    leftRightSpace: { value: number; visible: boolean };
    topBottomSpace: { value: number; visible: boolean };
}) => {
    const leftRightSpaceValue = leftRightSpace.visible ? leftRightSpace.value : 0;
    const topBottomSpaceValue = topBottomSpace.visible ? topBottomSpace.value : 0;
    const columnsWidth = (width - columnSpace * (columns.value - 1) - leftRightSpaceValue * 2) / columns.value;
    return (
        <div
            className="columns"
            style={{
                paddingLeft: `${leftRightSpaceValue}px`,
                paddingRight: `${leftRightSpaceValue}px`,
                paddingTop: `${topBottomSpaceValue}px`,
                paddingBottom: `${topBottomSpaceValue}px`,
            }}
        >
            {Array.from({ length: columns.value }).map((_, index) => (
                <div
                    key={index}
                    className="column"
                    style={{
                        width: `${columnsWidth}px`,
                        backgroundColor: 'rgba(11, 11, 255, 0.2)',
                        marginRight: `${columnSpace}px`,
                        height: '100%',
                    }}
                ></div>
            ))}
        </div>
    );
};

const Grid = ({
    width,
    height,
    grid,
}: {
    width: number;
    height: number;
    grid: { value: number; visible: boolean };
}) => {
    const lines = height / grid.value + 2;
    const columns = width / grid.value + 2;

    return (
        <div className="grid">
            <div className="rows">
                {Array.from({ length: lines }).map((_, index) => (
                    <div
                        key={index}
                        className="row"
                        style={{
                            height: `2px`,
                            width: '100%',
                            marginBottom: `${grid.value - 2}px`,
                            backgroundColor: 'rgba(0, 0, 0, 0.2)',
                            flexShrink: 0,
                        }}
                    ></div>
                ))}
            </div>
            <div className="columns">
                {Array.from({ length: columns }).map((_, index) => (
                    <div
                        key={index}
                        className="column"
                        style={{
                            width: `2px`,
                            height: '100%',
                            marginRight: `${grid.value - 2}px`,
                            backgroundColor: 'rgba(0, 0, 0, 0.2)',
                            flexShrink: 0,
                        }}
                    ></div>
                ))}
            </div>
        </div>
    );
};

const Padding = ({
    leftRightSpace,
    topBottomSpace,
}: {
    leftRightSpace: { value: number; visible: boolean };
    topBottomSpace: { value: number; visible: boolean };
}) => {
    const leftRightSpaceValue = leftRightSpace.visible ? leftRightSpace.value : 0;
    const topBottomSpaceValue = topBottomSpace.visible ? topBottomSpace.value : 0;
    return (
        <div
            style={{
                borderLeft: `${leftRightSpaceValue}px solid rgba(0, 255, 73, 0.2)`,
                borderRight: `${leftRightSpaceValue}px solid rgba(0, 255, 73, 0.2)`,
                borderTop: `${topBottomSpaceValue}px solid rgba(0, 255, 73, 0.2)`,
                borderBottom: `${topBottomSpaceValue}px solid rgba(0, 255, 73, 0.2)`,
                width: '100%',
                height: '100%',
                position: 'absolute',
                top: 0,
                left: 0,
                zIndex: 1000,
            }}
        ></div>
    );
};

const InnerPadding = ({
    innerRightSpace,
    innerBottomSpace,
}: {
    innerRightSpace: { value: number; visible: boolean };
    innerBottomSpace: { value: number; visible: boolean };
}) => {
    const innerRightSpaceValue = innerRightSpace.visible ? innerRightSpace.value : 0;
    const innerBottomSpaceValue = innerBottomSpace.visible ? innerBottomSpace.value : 0;
    return (
        <div
            style={{
                borderLeft: `${innerRightSpaceValue}px solid #3388ff4a`,
                borderRight: `${innerRightSpaceValue}px solid #3388ff4a`,
                borderTop: `${innerBottomSpaceValue}px solid #3388ff4a`,
                borderBottom: `${innerBottomSpaceValue}px solid #3388ff4a`,
                width: '100%',
                height: '100%',
                position: 'absolute',
                top: 0,
                left: 0,
                zIndex: 1001,
            }}
        ></div>
    );
};

const GridLines: React.FC<{ width: number; height: number }> = ({ width, height }) => {
    const gridInfo = useStageGridStore()((state) => state.gridInfo);

    if (!gridInfo) {
        return null;
    }

    const {
        grid,
        rows,
        columns,
        rowSpace,
        columnSpace,
        leftRightSpace,
        topBottomSpace,
        innerRightSpace,
        innerBottomSpace,
    } = gridInfo;

    return (
        <div className="c-grid-lines">
            {grid.visible && <Grid width={width} height={height} grid={grid} />}
            {rows.visible && (
                <Rows
                    width={width}
                    height={height}
                    rows={rows}
                    rowSpace={rowSpace}
                    topBottomSpace={topBottomSpace}
                    leftRightSpace={leftRightSpace}
                />
            )}
            {columns.visible && (
                <Columns
                    width={width}
                    height={height}
                    columns={columns}
                    columnSpace={columnSpace}
                    leftRightSpace={leftRightSpace}
                    topBottomSpace={topBottomSpace}
                />
            )}

            <Padding leftRightSpace={leftRightSpace} topBottomSpace={topBottomSpace} />

            <InnerPadding innerRightSpace={innerRightSpace} innerBottomSpace={innerBottomSpace} />
        </div>
    );
};

export default GridLines;
